import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { AiPageContext, AiSelectionContext } from '@/utils/aiPageContextFromPath';
import { getAiPageContextFromPath } from '@/utils/aiPageContextFromPath';

export interface AiAgentContextValue {
  drawerOpen: boolean;
  setDrawerOpen: (v: boolean) => void;
  toggleDrawer: () => void;
  /** Universal Intelligence Sidebar (proactive cards). */
  intelligenceSidebarOpen: boolean;
  setIntelligenceSidebarOpen: (v: boolean) => void;
  toggleIntelligenceSidebar: () => void;
  openExplain: () => void;
  /** Merged: explicit override (e.g. from a document page) or URL-derived + selection. */
  pageContext: AiPageContext;
  setPageContextOverride: (ctx: AiPageContext | null) => void;
  /** Set / clear list-row selection for contextual assistant / UDQ queries. */
  setAiSelection: (sel: AiSelectionContext | null) => void;
  /** Open the drawer and send this question (e.g. header "How-to" button). */
  askAssistant: (prompt: string) => void;
  /** Question queued by askAssistant; the drawer sends it and clears it. */
  pendingPrompt: string | null;
  clearPendingPrompt: () => void;
}

const Ctx = createContext<AiAgentContextValue | null>(null);

export function AiAgentProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [intelligenceSidebarOpen, setIntelligenceSidebarOpen] = useState(false);
  const [override, setOverride] = useState<AiPageContext | null>(null);
  const [selection, setSelection] = useState<AiSelectionContext | null>(null);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const askAssistant = useCallback((prompt: string) => {
    const p = prompt.trim();
    if (!p) return;
    setPendingPrompt(p);
    setDrawerOpen(true);
  }, []);
  const clearPendingPrompt = useCallback(() => setPendingPrompt(null), []);

  const fromPath = useMemo(() => getAiPageContextFromPath(location.pathname), [location.pathname]);
  const pageContext = useMemo(() => {
    const base = override != null ? { ...fromPath, ...override } : fromPath;
    return selection ? { ...base, selection } : base;
  }, [fromPath, override, selection]);

  // Clear selection when navigating to a different path
  useEffect(() => {
    setSelection(null);
  }, [location.pathname]);

  const toggleDrawer = useCallback(() => setDrawerOpen((o) => !o), []);
  const toggleIntelligenceSidebar = useCallback(
    () => setIntelligenceSidebarOpen((o) => !o),
    []
  );
  const openExplain = useCallback(() => setIntelligenceSidebarOpen(true), []);
  const setAiSelection = useCallback((sel: AiSelectionContext | null) => {
    setSelection(sel);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'A' || e.key === 'a')) {
        e.preventDefault();
        toggleDrawer();
      }
      if (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
        e.preventDefault();
        toggleIntelligenceSidebar();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleDrawer, toggleIntelligenceSidebar]);

  const value: AiAgentContextValue = {
    drawerOpen,
    setDrawerOpen,
    toggleDrawer,
    intelligenceSidebarOpen,
    setIntelligenceSidebarOpen,
    toggleIntelligenceSidebar,
    openExplain,
    pageContext,
    setPageContextOverride: setOverride,
    setAiSelection,
    askAssistant,
    pendingPrompt,
    clearPendingPrompt,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAiAgent(): AiAgentContextValue {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error('useAiAgent must be used within AiAgentProvider');
  }
  return v;
}

/** Same as useAiAgent but returns null outside AiAgentProvider (shared components, print views, tests). */
export function useOptionalAiAgent(): AiAgentContextValue | null {
  return useContext(Ctx);
}
